import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { booleanOptions, dateFormatYMD } from 'constants';
import { GridWrapper, PGridCenter } from 'components/Ui/styled';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { getSearchedStudents } from 'layouts/students/service/studentsApi';
import { getTutoringAvailability } from 'layouts/tutoring/services/tutoringApi';
import OutlinedButtonComponent from 'components/Common/OutlinedButtonComponent';
import { FormInputAutoCompleteSearch } from 'components/Common/Forms/FormAutoCompleteSearch';
import {
  getOptionLabel,
  getButtonSpinner,
  useNotification,
  showBackdropLoader,
} from 'utils/commonUtils';
import {
  setStudentId,
  setStudentName,
  setSubject,
  setSubjectName,
  setDate,
  setOverrideLimits,
  resetNewSessionFilter,
} from 'layouts/tutoring/store/newSessionFilterSlice/newSessionFilterSlice';
import BookSessionDialog from './BookSessionDialog';

const NewSessionTab = () => {
  const dispatch = useDispatch();
  const { NotificationPopup, handleErrorResponse } = useNotification();
  const [studentOptions, setStudentOptions] = useState([]);
  const [student, setStudent] = useState(null);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [isStudentSearchloading, setIsStudentSearchloading] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [isLoadingBackDrop, setIsLoadingBackDrop] = useState(false);
  const [bookSessionDialog, setBookSessionDialog] = useState(null);
  const [bookSessionForm, setBookSessionForm] = useState(null);
  const MIN_SEARCH_LENGTH = 1;

  const subjects = useSelector((state) => state?.subjects);
  const newSessionFilters = useSelector((state) => state?.newSessionFilters);

  const { studentId, subject, date, overrideLimits } = newSessionFilters || {};

  const dateInfo = date ? format(new Date(date), dateFormatYMD) : '';

  const subjectOptions = subjects?.map((item) => ({
    id: item.subjectId,
    value: item.name,
  }));

  const defaultValue = {
    studentId: studentId,
    subject: subject,
    date: date,
    overrideLimits: overrideLimits,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const { control, register, setValue } = useFunction;

  useEffect(() => {
    fetchSearchedStudents();
  }, [studentSearchQuery]);

  const fetchSearchedStudents = async () => {
    try {
      if (studentSearchQuery?.length >= MIN_SEARCH_LENGTH) {
        setIsStudentSearchloading(true);
        const searchedStudents = await getSearchedStudents(
          studentSearchQuery,
          true,
          false,
          20
        );
        setStudentOptions(searchedStudents?.items);
        setIsStudentSearchloading(false);
      }
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    if (student) {
      const studentName = `${student?.firstName} ${student?.lastName}`;
      dispatch(setStudentId(student?.studentId));
      dispatch(setStudentName(studentName));
    }
  }, [student]);

  useEffect(() => {
    if (student && subject && date) {
      fetchTutoringAvailability();
    }
  }, [student, subject, date, overrideLimits]);

  const fetchTutoringAvailability = async () => {
    try {
      setIsLoadingBackDrop(true);
      const tutoringAvailability = await getTutoringAvailability(
        student?.studentId,
        subject,
        dateInfo,
        overrideLimits
      );
      setTimeSlots(tutoringAvailability?.timeSlots);
      setIsLoadingBackDrop(false);
    } catch (error) {
      setIsLoadingBackDrop(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleSubject = (e) => {
    dispatch(setSubject(e.target.value));
    const subjectName =
      subjects.find((subject) => e.target.value.includes(subject.subjectId))
        ?.name || '';
    dispatch(setSubjectName(subjectName));
  };

  const handleDate = (e) => {
    dispatch(setDate(e.target.value));
  };

  const handleOverrideLimits = (e) => {
    dispatch(setOverrideLimits(e.target.value));
  };

  const handleTimeSlot = (timeSlot) => {
    if (student && subject && date) {
      setBookSessionDialog(true);
      setSelectedTimeSlot(timeSlot.timeSlotId);
      setBookSessionForm(timeSlot);
    } else {
      // setOpenNotification(true);
      // setNotificationMessage(selectSubjectDate);
    }
  };

  const handleClearFilters = () => {
    setStudentOptions([]);
    setStudent(null);
    setStudentSearchQuery('');
    dispatch(resetNewSessionFilter());
    setValue('subject', '');
    setValue('overrideLimits', false);
    setTimeSlots([]);
  };

  return (
    <>
      {showBackdropLoader(isLoadingBackDrop)}
      <MDBox mt={1}></MDBox>
      <GridWrapper container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormInputAutoCompleteSearch
            label="Search for student by name or ID"
            placeholder="Enter name or ID..."
            options={studentOptions}
            setSelectedInput={setStudent}
            setInputSearchQuery={setStudentSearchQuery}
            isSearchloading={isStudentSearchloading}
            getOptionLabel={getOptionLabel}
            value={student}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInputDropdown
            name="subject"
            type="text"
            control={control}
            options={subjectOptions}
            defaultValue={defaultValue?.subject}
            label="Subject"
            register={register('subject', {
              onChange: (e) => {
                handleSubject(e);
              },
            })}
          />
        </Grid>
      </GridWrapper>
      <GridWrapper container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FormInputDate
            name="date"
            control={control}
            label="Date"
            value={date}
            register={register('date', {
              onChange: (e) => {
                handleDate(e);
              },
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormInputDropdown
            name="overrideLimits"
            type="text"
            control={control}
            options={booleanOptions}
            defaultValue={defaultValue?.overrideLimits}
            label="Override Limits"
            register={register('overrideLimits', {
              onChange: (e) => {
                handleOverrideLimits(e);
              },
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {getButtonSpinner(false, handleClearFilters, 'Clear Filters')}
        </Grid>
      </GridWrapper>
      <MDBox pt={1}></MDBox>
      <GridWrapper container spacing={2}>
        <Grid item xs={12} md={7}>
          <Grid container spacing={1}>
            {timeSlots?.map((timeSlot, index) => (
              <PGridCenter item xs={12} sm={4} key={index}>
                <OutlinedButtonComponent
                  handleClick={() => handleTimeSlot(timeSlot)}
                  selectedItem={selectedTimeSlot}
                  item={timeSlot}
                  id={timeSlot.timeSlotId}
                  primary={!timeSlot.available}
                />
              </PGridCenter>
            ))}
          </Grid>
        </Grid>
      </GridWrapper>
      <BookSessionDialog
        bookSessionDialog={bookSessionDialog}
        setBookSessionDialog={setBookSessionDialog}
        bookSessionForm={bookSessionForm}
        student={student}
        fetchTutoringAvailability={fetchTutoringAvailability}
      />
      <NotificationPopup />
    </>
  );
};

export default NewSessionTab;
